/**
 * widgetFormSchemas.js
 * 
 * Declarative form schema registry for 9 widget types.
 * Provides field definitions, default values, and helper functions for dynamic form rendering.
 */

// ============================================================================
// SHARED OPTIONS
// ============================================================================

export const COLOR_OPTIONS = [
  { value: 'red', label: '빨강' },
  { value: 'orange', label: '주황' },
  { value: 'emerald', label: '초록' },
  { value: 'indigo', label: '인디고' },
  { value: 'slate', label: '회색' },
  { value: 'blue', label: '파랑' },
  { value: 'purple', label: '보라' },
  { value: 'cyan', label: '청록' },
  { value: 'pink', label: '분홍' },
  { value: 'yellow', label: '노랑' },
];

export const CHART_TYPE_OPTIONS = [
  { value: 'pie', label: '파이 차트' },
  { value: 'donut', label: '도넛 차트' },
  { value: 'bar', label: '막대 차트 (세로)' },
  { value: 'horizontal_bar', label: '막대 차트 (가로)' },
  { value: 'line', label: '라인 차트' },
  { value: 'gauge', label: '게이지 차트' },
  { value: 'radar', label: '레이더 차트' },
];

export const ICON_OPTIONS = [
  'Settings', 'Warning', 'History', 'FactCheck', 'Build', 'Description',
  'BarChart', 'PieChart', 'Timeline', 'Assessment', 'Dashboard', 'Inventory',
  'Engineering', 'Security', 'Speed', 'CheckCircle', 'Error', 'Info',
  'CalendarToday', 'Schedule', 'Notifications', 'Search', 'Visibility',
  'PictureAsPdf', 'Image', 'AttachFile', 'CloudDownload', 'Storage',
  'Memory', 'DeviceHub', 'Thermostat', 'WaterDrop', 'Air', 'Bolt',
  'LocalShipping', 'Factory', 'Handyman', 'Construction', 'Science',
  'LocalOffer', 'Label', 'Bookmark', 'Star', 'Favorite', 'ThumbUp',
];

export const WIDGET_TYPE_OPTIONS = [
  { value: 'info_list', label: '정보 목록', description: '키-값 형태의 정보 표시', icon: 'FormatListBulleted' },
  { value: 'metric_grid', label: '메트릭 그리드', description: '수치 지표를 그리드로 표시', icon: 'GridView' },
  { value: 'progress_bar', label: '프로그레스 바', description: '진행률 바 표시', icon: 'LinearScale' },
  { value: 'document_card', label: '문서 카드', description: '문서/파일 카드 표시', icon: 'Description' },
  { value: 'date_info', label: '날짜 정보', description: '날짜 및 D-day 표시', icon: 'CalendarToday' },
  { value: 'chart', label: '차트', description: 'ECharts 기반 차트', icon: 'BarChart' },
  { value: 'data_table', label: '데이터 테이블', description: '인라인 데이터 테이블', icon: 'TableChart' },
  { value: 'timeline', label: '타임라인', description: '이벤트 타임라인', icon: 'Timeline' },
  { value: 'image_card', label: '이미지 카드', description: '이미지/첨부파일 카드', icon: 'Image' },
];

// ============================================================================
// WIDGET SCHEMAS
// ============================================================================

export const WIDGET_SCHEMAS = {
  // --------------------------------------------------------------------------
  // INFO_LIST
  // --------------------------------------------------------------------------
  info_list: {
    optionFields: [
      {
        key: 'title',
        label: '제목',
        type: 'text',
        placeholder: '예: 장비 기본 정보',
      },
    ],
    hasItems: true,
    itemFields: [
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        required: true,
        placeholder: '예: 장비명',
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        placeholder: '예: machine_info',
        helpText: '연결할 데이터 소스를 선택하세요',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: machine_name',
        helpText: '데이터 소스가 여러 컬럼을 반환할 때, 표시할 컬럼명',
      },
      {
        key: 'format',
        label: '포맷',
        type: 'text',
        placeholder: '예: {field1} / {field2}',
        helpText: '{컬럼명} 형식으로 여러 컬럼 조합 가능',
      },
    ],
  },

  // --------------------------------------------------------------------------
  // METRIC_GRID
  // --------------------------------------------------------------------------
  metric_grid: {
    optionFields: [
      {
        key: 'title',
        label: '제목',
        type: 'text',
        placeholder: '예: 위험성 평가 현황',
      },
      {
        key: 'columns',
        label: '열 개수',
        type: 'select',
        options: [
          { value: 1, label: '1열' },
          { value: 2, label: '2열' },
          { value: 3, label: '3열' },
        ],
        defaultValue: 3,
      },
    ],
    hasItems: true,
    hasColorRules: true,
    colorRulesTarget: 'items',
    itemFields: [
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        required: true,
        placeholder: '예: 고위험',
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: risk_count_high',
        helpText: '연결할 데이터 소스를 선택하세요',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: count',
        helpText: '데이터 소스가 여러 컬럼을 반환할 때, 표시할 컬럼명',
      },
      {
        key: 'unit',
        label: '단위',
        type: 'text',
        placeholder: '예: 건',
      },
      {
        key: 'color',
        label: '기본 색상',
        type: 'color',
        options: COLOR_OPTIONS,
        defaultValue: 'orange',
        helpText: '조건부 색상 규칙이 없거나 매치되지 않을 때 사용됩니다',
      },
      {
        key: 'showDot',
        label: '점 표시',
        type: 'boolean',
        defaultValue: false,
      },
    ],
    hasOnClick: true,
    onClickFields: [
      {
        key: 'type',
        label: '클릭 액션',
        type: 'select',
        options: [
          { value: '', label: '없음' },
          { value: 'update', label: '값 수정 (UPDATE)' },
        ],
      },
      {
        key: 'source_key',
        label: '대상 데이터 소스',
        type: 'text',
        placeholder: '비워두면 항목의 source_key 사용',
        helpText: '수정할 데이터 소스 (column 타입만 지원)',
        condition: (ctx) => ctx?.type === 'update',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: value (비워두면 기본값 사용)',
        helpText: '대상 데이터 소스가 객체/행일 때 수정할 컬럼명',
        condition: (ctx) => ctx?.type === 'update',
      },
      {
        key: 'inputType',
        label: '입력 타입',
        type: 'select',
        options: [
          { value: 'number', label: '숫자' },
          { value: 'text', label: '텍스트' },
        ],
        defaultValue: 'number',
        condition: (ctx) => ctx?.type === 'update',
      },
      {
        key: 'unit',
        label: '단위 (입력창)',
        type: 'text',
        placeholder: '비워두면 항목 단위 사용',
        condition: (ctx) => ctx?.type === 'update',
      },
    ],
    colorRuleFields: [
      {
        key: 'op',
        label: '연산자',
        type: 'select',
        options: [
          { value: '>', label: '> (초과)' },
          { value: '>=', label: '>= (이상)' },
          { value: '<', label: '< (미만)' },
          { value: '<=', label: '<= (이하)' },
          { value: '==', label: '== (같음)' },
          { value: '!=', label: '!= (다름)' },
        ],
      },
      {
        key: 'value',
        label: '기준값',
        type: 'number',
        placeholder: '예: 5',
      },
      {
        key: 'color',
        label: '적용 색상',
        type: 'color',
        options: COLOR_OPTIONS,
      },
    ],
  },

  // --------------------------------------------------------------------------
  // PROGRESS_BAR
  // --------------------------------------------------------------------------
  progress_bar: {
    optionFields: [
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: maintenance_compliance',
        helpText: '진행률 값 (0-100)을 반환하는 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: progress',
        helpText: '데이터 소스가 여러 컬럼을 반환할 때, 진행률 컬럼명',
      },
      {
        key: 'color',
        label: '기본 색상',
        type: 'color',
        options: COLOR_OPTIONS,
        defaultValue: 'orange',
        helpText: '조건부 색상 규칙이 없거나 매치되지 않을 때 사용됩니다',
      },
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        placeholder: '예: 작업 진행률',
      },
    ],
    hasItems: false,
    hasColorRules: true,
    colorRulesTarget: 'options',
    colorRuleFields: [
      {
        key: 'op',
        label: '연산자',
        type: 'select',
        options: [
          { value: '>', label: '> (초과)' },
          { value: '>=', label: '>= (이상)' },
          { value: '<', label: '< (미만)' },
          { value: '<=', label: '<= (이하)' },
          { value: '==', label: '== (같음)' },
          { value: '!=', label: '!= (다름)' },
        ],
      },
      {
        key: 'value',
        label: '기준값',
        type: 'number',
        placeholder: '예: 80',
      },
      {
        key: 'color',
        label: '적용 색상',
        type: 'color',
        options: COLOR_OPTIONS,
      },
    ],
  },

  // --------------------------------------------------------------------------
  // DOCUMENT_CARD
  // --------------------------------------------------------------------------
  document_card: {
    optionFields: [
      {
        key: 'title',
        label: '제목',
        type: 'text',
        required: true,
        placeholder: '예: 인증서',
      },
      {
        key: 'subtitle',
        label: '부제목',
        type: 'text',
        placeholder: '예: ISO 9001',
      },
      {
        key: 'icon',
        label: '아이콘',
        type: 'icon',
        options: ICON_OPTIONS,
        defaultValue: 'Description',
      },
      {
        key: 'iconColor',
        label: '아이콘 색상',
        type: 'color',
        options: COLOR_OPTIONS,
      },
      {
        key: 'actionType',
        label: '액션 타입',
        type: 'select',
        options: [
          { value: 'downloadCert', label: '인증서 다운로드' },
          { value: 'openMsds', label: 'MSDS 열기' },
          { value: 'openLink', label: '링크 열기' },
        ],
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        placeholder: '예: cert_url',
        helpText: '액션에 사용할 URL 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: url',
        helpText: '데이터 소스가 객체를 반환할 때, URL 컬럼명',
      },
    ],
    hasItems: false,
  },

  // --------------------------------------------------------------------------
  // DATE_INFO
  // --------------------------------------------------------------------------
  date_info: {
    optionFields: [
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        required: true,
        placeholder: '예: 만료일',
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: last_inspection_date',
        helpText: '날짜 값 (YYYY-MM-DD)을 반환하는 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: inspection_date',
        helpText: '데이터 소스가 여러 컬럼을 반환할 때, 날짜 컬럼명',
      },
      {
        key: 'showDday',
        label: 'D-day 표시',
        type: 'boolean',
        defaultValue: true,
      },
      {
        key: 'color',
        label: '색상',
        type: 'color',
        options: COLOR_OPTIONS,
        defaultValue: 'orange',
      },
    ],
    hasItems: false,
  },

  // --------------------------------------------------------------------------
  // CHART
  // --------------------------------------------------------------------------
  chart: {
    optionFields: [
      {
        key: 'chartType',
        label: '차트 타입',
        type: 'select',
        options: CHART_TYPE_OPTIONS,
        required: true,
      },
      {
        key: 'height',
        label: '높이',
        type: 'number',
        defaultValue: 200,
        placeholder: '200',
        helpText: '차트 높이 (px)',
      },
      {
        key: 'title',
        label: '제목',
        type: 'text',
        placeholder: '예: 생산 현황',
      },
      {
        key: 'max',
        label: '최대값',
        type: 'number',
        placeholder: '100',
        helpText: '게이지 차트의 최대값',
        condition: (opts) => opts.chartType === 'gauge',
      },
      {
        key: 'unit',
        label: '단위',
        type: 'text',
        placeholder: '예: %',
      },
      {
        key: 'barWidth',
        label: '막대 너비',
        type: 'number',
        placeholder: '20',
        helpText: '막대 차트의 막대 너비',
        condition: (opts) => ['bar', 'horizontal_bar'].includes(opts.chartType),
      },
      {
        key: 'yMin',
        label: 'Y축 최소값',
        type: 'number',
        placeholder: '자동',
        helpText: '비워두면 데이터 기반 자동 계산 (0부터 시작하지 않음)',
        condition: (opts) => ['line', 'bar'].includes(opts.chartType),
      },
      {
        key: 'yMax',
        label: 'Y축 최대값',
        type: 'number',
        placeholder: '자동',
        helpText: '비워두면 데이터 기반 자동 계산',
        condition: (opts) => ['line', 'bar'].includes(opts.chartType),
      },
      {
        key: 'radarShape',
        label: '레이더 모양',
        type: 'select',
        options: [
          { value: 'polygon', label: '다각형' },
          { value: 'circle', label: '원형' },
        ],
        defaultValue: 'polygon',
        condition: (opts) => opts.chartType === 'radar',
      },
      {
        key: 'radarFill',
        label: '레이더 영역 채우기',
        type: 'boolean',
        defaultValue: true,
        condition: (opts) => opts.chartType === 'radar',
      },
    ],
    hasItems: true,
    itemFields: [
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        required: true,
        placeholder: '예: 생산량',
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: production_count',
        helpText: '연결할 데이터 소스를 선택하세요',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: value',
        helpText: '데이터 소스가 여러 컬럼을 반환할 때, 값 컬럼명',
      },
      {
        key: 'color',
        label: '색상',
        type: 'color',
        options: COLOR_OPTIONS,
      },
      {
        key: 'labelKey',
        label: 'X축 키',
        type: 'text',
        placeholder: 'label',
        helpText: '시계열 데이터의 X축(라벨) 필드명 (기본: label)',
        condition: (ctx) => ['line'].includes(ctx?._parentOptions?.chartType),
      },
      {
        key: 'valueKey',
        label: 'Y축 키',
        type: 'text',
        placeholder: 'value',
        helpText: '시계열 데이터의 Y축(값) 필드명 (기본: value)',
        condition: (ctx) => ['line'].includes(ctx?._parentOptions?.chartType),
      },
      {
        key: 'max',
        label: '최대값',
        type: 'number',
        placeholder: '100',
        helpText: '레이더 차트의 축 최대값',
        condition: (ctx) => ctx?._parentOptions?.chartType === 'radar',
      },
    ],
    hasReferenceLines: true,
    referenceLineCondition: (opts) => ['line', 'bar', 'horizontal_bar'].includes(opts.chartType),
    referenceLineFields: [
      {
        key: 'type',
        label: '타입',
        type: 'select',
        options: [
          { value: 'fixed', label: '고정값' },
          { value: 'average', label: '평균' },
          { value: 'source', label: '데이터 소스' },
        ],
        defaultValue: 'fixed',
      },
      {
        key: 'value',
        label: '고정 값',
        type: 'number',
        placeholder: '예: 80',
        condition: (ctx) => ctx?.type === 'fixed' || !ctx?.type,
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        placeholder: '예: target_value',
        helpText: '기준선 값을 가져올 데이터 소스',
        condition: (ctx) => ctx?.type === 'source',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: value',
        helpText: '데이터 소스가 객체일 때 값 컬럼',
        condition: (ctx) => ctx?.type === 'source',
      },
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        placeholder: '예: 목표',
      },
      {
        key: 'color',
        label: '색상',
        type: 'color',
        options: COLOR_OPTIONS,
        defaultValue: 'red',
      },
      {
        key: 'lineStyle',
        label: '선 스타일',
        type: 'select',
        options: [
          { value: 'solid', label: '실선' },
          { value: 'dashed', label: '점선' },
          { value: 'dotted', label: '점점선' },
        ],
        defaultValue: 'dashed',
      },
    ],
  },

  // --------------------------------------------------------------------------
  // DATA_TABLE
  // --------------------------------------------------------------------------
  data_table: {
    optionFields: [
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: maintenance_log',
        helpText: '배열 데이터를 반환하는 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: records',
        helpText: '데이터 소스가 객체를 반환할 때, 배열 컬럼명',
      },
      {
        key: 'maxRows',
        label: '최대 행 수',
        type: 'number',
        defaultValue: 5,
        placeholder: '5',
      },
      {
        key: 'emptyMessage',
        label: '빈 메시지',
        type: 'text',
        defaultValue: '데이터 없음',
        placeholder: '데이터 없음',
      },
      {
        key: 'title',
        label: '제목',
        type: 'text',
        placeholder: '예: 작업 목록',
      },
    ],
    hasItems: false,
    hasColumns: true,
    columnFields: [
      {
        key: 'header',
        label: '헤더',
        type: 'text',
        required: true,
        placeholder: '예: 작업명',
      },
      {
        key: 'key',
        label: '키',
        type: 'text',
        required: true,
        placeholder: '예: task_name',
      },
      {
        key: 'width',
        label: '너비',
        type: 'text',
        placeholder: '80px',
        helpText: 'CSS 너비 값 (예: 80px, 20%)',
      },
      {
        key: 'align',
        label: '정렬',
        type: 'select',
        options: [
          { value: 'left', label: '왼쪽' },
          { value: 'center', label: '가운데' },
          { value: 'right', label: '오른쪽' },
        ],
        defaultValue: 'left',
      },
      {
        key: 'badge',
        label: '뱃지 표시',
        type: 'boolean',
        defaultValue: false,
      },
      {
        key: 'badgeColors',
        label: '뱃지 색상 맵',
        type: 'json',
        placeholder: '{"완료":"emerald","진행중":"orange"}',
        helpText: '{"값":"색상"} JSON 형식',
        condition: (col) => col.badge === true,
      },
    ],
  },

  // --------------------------------------------------------------------------
  // TIMELINE
  // --------------------------------------------------------------------------
  timeline: {
    optionFields: [
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: maintenance_events',
        helpText: '이벤트 배열 데이터를 반환하는 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: events',
        helpText: '데이터 소스가 객체를 반환할 때, 배열 컬럼명',
      },
      {
        key: 'maxItems',
        label: '최대 항목 수',
        type: 'number',
        defaultValue: 5,
        placeholder: '5',
      },
      {
        key: 'dateKey',
        label: '날짜 키',
        type: 'text',
        defaultValue: 'date',
        placeholder: 'date',
        helpText: '각 항목의 날짜 필드명',
      },
      {
        key: 'titleKey',
        label: '제목 키',
        type: 'text',
        defaultValue: 'title',
        placeholder: 'title',
        helpText: '각 항목의 제목 필드명',
      },
      {
        key: 'descriptionKey',
        label: '설명 키',
        type: 'text',
        defaultValue: 'description',
        placeholder: 'description',
        helpText: '각 항목의 설명 필드명',
      },
      {
        key: 'color',
        label: '색상',
        type: 'color',
        options: COLOR_OPTIONS,
        defaultValue: 'indigo',
      },
      {
        key: 'emptyMessage',
        label: '빈 메시지',
        type: 'text',
        placeholder: '이벤트 없음',
      },
      {
        key: 'title',
        label: '제목',
        type: 'text',
        placeholder: '예: 작업 이력',
      },
    ],
    hasItems: false,
  },

  // --------------------------------------------------------------------------
  // IMAGE_CARD
  // --------------------------------------------------------------------------
  image_card: {
    optionFields: [
      {
        key: 'title',
        label: '제목',
        type: 'text',
        placeholder: '예: 제품 이미지',
      },
      {
        key: 'imageUrl',
        label: '이미지 URL',
        type: 'text',
        placeholder: 'https://...',
        helpText: '단일 이미지 URL (정적)',
      },
      {
        key: 'fallbackIcon',
        label: '대체 아이콘',
        type: 'icon',
        options: ICON_OPTIONS,
        defaultValue: 'Image',
      },
      {
        key: 'iconColor',
        label: '아이콘 색상',
        type: 'color',
        options: COLOR_OPTIONS,
      },
      {
        key: 'height',
        label: '높이',
        type: 'number',
        defaultValue: 200,
        placeholder: '200',
        helpText: '이미지 높이 (px)',
      },
      {
        key: 'objectFit',
        label: '이미지 맞춤',
        type: 'select',
        options: [
          { value: 'cover', label: 'Cover' },
          { value: 'contain', label: 'Contain' },
          { value: 'fill', label: 'Fill' },
        ],
        defaultValue: 'cover',
      },
      {
        key: 'caption',
        label: '캡션',
        type: 'text',
        placeholder: '예: 제품 외관',
      },
      {
        key: 'actionType',
        label: '액션 타입',
        type: 'select',
        options: [
          { value: 'openLink', label: '링크 열기' },
          { value: 'download', label: '다운로드' },
        ],
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        placeholder: '예: machine_image',
        helpText: '동적 이미지 URL 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: image_url',
        helpText: '데이터 소스가 객체를 반환할 때, URL 컬럼명',
      },
      {
        key: 'columns',
        label: '열 개수',
        type: 'number',
        defaultValue: 2,
        placeholder: '2',
        helpText: '다중 이미지 모드의 그리드 열 수',
      },
    ],
    hasItems: true,
    itemFields: [
      {
        key: 'label',
        label: '라벨',
        type: 'text',
        placeholder: '예: 제품 A',
      },
      {
        key: 'source_key',
        label: '데이터 소스',
        type: 'text',
        required: true,
        placeholder: '예: product_image',
        helpText: '이미지 URL 데이터 소스',
      },
      {
        key: 'dataKey',
        label: '컬럼명',
        type: 'text',
        placeholder: '예: url',
        helpText: '데이터 소스가 객체를 반환할 때, URL 컬럼명',
      },
      {
        key: 'fallbackIcon',
        label: '대체 아이콘',
        type: 'icon',
        options: ICON_OPTIONS,
      },
      {
        key: 'iconColor',
        label: '아이콘 색상',
        type: 'color',
        options: COLOR_OPTIONS,
      },
      {
        key: 'actionType',
        label: '액션 타입',
        type: 'select',
        options: [
          { value: 'openLink', label: '링크 열기' },
          { value: 'download', label: '다운로드' },
        ],
      },
    ],
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get schema for a specific widget type
 * @param {string} type - Widget type
 * @returns {object|null} Schema definition or null if not found
 */
export function getSchemaForType(type) {
  return WIDGET_SCHEMAS[type] || null;
}

/**
 * Build default options object from schema optionFields
 * @param {string} type - Widget type
 * @returns {object} Default options object
 */
export function getDefaultOptions(type) {
  const schema = getSchemaForType(type);
  if (!schema) return {};

  const defaults = {};
  
  if (schema.optionFields) {
    schema.optionFields.forEach((field) => {
      // Skip conditional fields — they'll be set when their condition is met
      if (field.condition) return;
      if (field.defaultValue !== undefined) {
        defaults[field.key] = field.defaultValue;
      }
    });
  }

  // Special handling for data_table columns
  if (schema.hasColumns) {
    defaults.columns = [];
  }

  // Special handling for chart referenceLines
  if (schema.hasReferenceLines) {
    defaults.referenceLines = [];
  }

  return defaults;
}

/**
 * Build default item object from schema itemFields
 * @param {string} type - Widget type
 * @returns {object} Default item object
 */
export function getDefaultItem(type) {
  const schema = getSchemaForType(type);
  if (!schema || !schema.hasItems) return {};

  const defaults = {};
  
  if (schema.itemFields) {
    schema.itemFields.forEach((field) => {
      // Skip conditional fields — they'll be set when their condition is met
      if (field.condition) return;
      if (field.defaultValue !== undefined) {
        defaults[field.key] = field.defaultValue;
      } else {
        // Set appropriate default based on type
        switch (field.type) {
          case 'boolean':
            defaults[field.key] = false;
            break;
          case 'number':
            defaults[field.key] = 0;
            break;
          default:
            defaults[field.key] = '';
        }
      }
    });
  }

  return defaults;
}

/**
 * Build default column object for data_table
 * @returns {object} Default column object
 */
export function getDefaultColumn() {
  const schema = getSchemaForType('data_table');
  if (!schema || !schema.columnFields) return {};

  const defaults = {};
  
  schema.columnFields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    } else {
      switch (field.type) {
        case 'boolean':
          defaults[field.key] = false;
          break;
        case 'number':
          defaults[field.key] = 0;
          break;
        default:
          defaults[field.key] = '';
      }
    }
  });

  return defaults;
}

/**
 * Build default color rule object
 * @returns {object} Default color rule object { op, value, color }
 */
export function getDefaultColorRule() {
  return { op: '>', value: 0, color: 'red' };
}

/**
 * Build default reference line object for chart
 * @returns {object} Default reference line object
 */
export function getDefaultReferenceLine() {
  const schema = getSchemaForType('chart');
  if (!schema || !schema.referenceLineFields) return {};

  const defaults = {};
  
  schema.referenceLineFields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      defaults[field.key] = field.defaultValue;
    } else {
      switch (field.type) {
        case 'boolean':
          defaults[field.key] = false;
          break;
        case 'number':
          defaults[field.key] = 0;
          break;
        default:
          defaults[field.key] = '';
      }
    }
  });

  return defaults;
}

// NOTE: flattenSubHeaders/expandSubHeaders removed (Phase 9 — subHeader feature deprecated)
